/**
 * AnalyticsView — Operational Business Intelligence
 *
 * A dedicated analytics window covering:
 *   • Transaction analytics (volume, success rate, declines)
 *   • Zone SLA compliance vs SIB 92% target
 *   • MTTR trend with anomaly markers
 *   • Alert volume distribution (P1–P4 stacked)
 *   • Component fault breakdown (horizontal bar)
 *   • Fleet Health Radar (4-zone × 5-axis)
 *   • Zone Comparative Table (sortable)
 *
 * Uses recharts + seeded synthetic time-series layered over live fleet data.
 */

import { useMemo, useState, type CSSProperties, type ReactNode } from 'react'
import {
  AreaChart, Area,
  BarChart, Bar,
  ComposedChart, Line,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine, Cell,
} from 'recharts'
import { useATMStore } from '../store/atmStore'
import { useTheme } from '../hooks/useTheme'
import type { ATMDevice, ATMAlert } from '@atmvision/types'

// ─── Palette / constants ──────────────────────────────────────────────────────

const ZONE_NAMES = ['Distrito Nacional', 'Norte', 'Sur', 'Este'] as const
type ZoneName = (typeof ZONE_NAMES)[number]
type TimeRange = '7d' | '30d' | '90d'

const ZONE_SHORT: Record<ZoneName, string> = {
  'Distrito Nacional': 'D.N.', Norte: 'Norte', Sur: 'Sur', Este: 'Este',
}
const ZONE_HEX: Record<ZoneName, string> = {
  'Distrito Nacional': '#264E72', Norte: '#02AFF0', Sur: '#F89421', Este: '#e74c3c',
}
const COMP_LABELS = ['CDM', 'NET', 'PTR', 'IDC', 'UPS'] as const
const COMP_HEX    = ['#ef4444', '#F89421', '#02AFF0', '#a855f7', '#22c55e']
const ALERT_HEX   = { P1: '#ef4444', P2: '#f97316', P3: '#F89421', P4: '#02AFF0' }
const RANGE_DAYS: Record<TimeRange, number> = { '7d': 7, '30d': 30, '90d': 90 }

// ─── RNG (seeded, deterministic) ──────────────────────────────────────────────

function rng32(seed: number): () => number {
  let s = (seed ^ 0xDEADBEEF) >>> 0
  return () => {
    s |= 0; s = (s + 0x6D2B79F5) | 0
    let t = Math.imul(s ^ (s >>> 15), 1 | s)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

// ─── Data builders ────────────────────────────────────────────────────────────

function buildTxSeries(range: TimeRange) {
  const days = RANGE_DAYS[range]
  const r = rng32(days * 17)
  return Array.from({ length: days }, (_, i) => {
    const base   = 58_000 + r() * 28_000
    const txns   = Math.round(base)
    const sRate  = 0.924 + r() * 0.05
    const success  = Math.round(txns * sRate)
    const declined = Math.round(txns * (0.028 + r() * 0.02))
    const timeout  = Math.max(0, txns - success - declined)
    const d = new Date(Date.now() - (days - 1 - i) * 86_400_000)
    return {
      label: d.toLocaleDateString('es-DO', { day: '2-digit', month: 'short' }),
      txns, success, declined, timeout,
      rate: +(success / txns * 100).toFixed(1),
    }
  })
}

function buildMTTRSeries(range: TimeRange) {
  const days = RANGE_DAYS[range]
  const r = rng32(days * 31)
  return Array.from({ length: days }, (_, i) => {
    const mttr = Math.round(95 + r() * 110)
    const d = new Date(Date.now() - (days - 1 - i) * 86_400_000)
    return { label: d.toLocaleDateString('es-DO', { day: '2-digit', month: 'short' }), mttr, target: 120 }
  })
}

function buildAlertSeries(range: TimeRange) {
  const days = RANGE_DAYS[range]
  const r = rng32(days * 53)
  return Array.from({ length: days }, (_, i) => {
    const d = new Date(Date.now() - (days - 1 - i) * 86_400_000)
    return {
      label: d.toLocaleDateString('es-DO', { day: '2-digit', month: 'short' }),
      P1: Math.floor(1 + r() * 5),
      P2: Math.floor(3 + r() * 9),
      P3: Math.floor(6 + r() * 13),
      P4: Math.floor(12 + r() * 28),
    }
  })
}

function buildZoneAvail(fleet: ATMDevice[]) {
  const map = new Map<string, { total: number; avail: number }>()
  fleet.forEach((d) => {
    const z = (d.zone as ZoneName) ?? 'Distrito Nacional'
    const e = map.get(z) ?? { total: 0, avail: 0 }
    e.total++
    if (d.status === 'IN_SERVICE') e.avail++
    map.set(z, e)
  })
  return ZONE_NAMES.map((z) => {
    const e = map.get(z)
    const pct = e && e.total > 0 ? +(e.avail / e.total * 100).toFixed(1) : (z === 'Este' ? 92.9 : z === 'Distrito Nacional' ? 92.0 : z === 'Norte' ? 89.2 : 88.9)
    return { zone: ZONE_SHORT[z], fullZone: z, pct, color: ZONE_HEX[z], target: 92 }
  })
}

function buildComponentFaults(fleet: ATMDevice[], range: TimeRange) {
  const mul = range === '7d' ? 1 : range === '30d' ? 4.2 : 12.5
  const faulted = fleet.filter((d) => d.status === 'OUT_OF_SERVICE' || d.status === 'WARNING').length || 86
  const weights = [0.38, 0.25, 0.17, 0.12, 0.08]
  const r = rng32(Math.floor(mul * 100))
  return COMP_LABELS.map((comp, i) => {
    const n = Math.round(faulted * mul * weights[i] * (0.88 + r() * 0.24))
    return { component: comp, faults: n, rate: +(n / fleet.length * 100 || 0).toFixed(1), color: COMP_HEX[i] }
  }).sort((a, b) => b.faults - a.faults)
}

function buildRadar(fleet: ATMDevice[]) {
  const map = new Map<string, { total: number; avail: number; oos: number }>()
  fleet.forEach((d) => {
    const short = ZONE_SHORT[(d.zone as ZoneName)] ?? 'D.N.'
    const e = map.get(short) ?? { total: 0, avail: 0, oos: 0 }
    e.total++
    if (d.status === 'IN_SERVICE') e.avail++
    if (d.status === 'OUT_OF_SERVICE') e.oos++
    map.set(short, e)
  })
  const a = (z: string) => {
    const e = map.get(z)
    return e && e.total > 0 ? +(e.avail / e.total * 100).toFixed(1) : 90
  }
  const r = rng32(8675309)
  const jitter = () => +(r() * 8 - 4).toFixed(1)
  return [
    { axis: 'Disponibilidad', 'D.N.': a('D.N.'),           Norte: a('Norte'),           Sur: a('Sur'),           Este: a('Este')           },
    { axis: 'MTTR Score',     'D.N.': +(85 + jitter()),    Norte: +(78 + jitter()),    Sur: +(74 + jitter()),    Este: +(88 + jitter())    },
    { axis: 'Confiabilidad',  'D.N.': +(91 + jitter()),    Norte: +(86 + jitter()),    Sur: +(84 + jitter()),    Este: +(92 + jitter())    },
    { axis: 'Tx Efficiency',  'D.N.': +(93 + jitter()),    Norte: +(87 + jitter()),    Sur: +(85 + jitter()),    Este: +(91 + jitter())    },
    { axis: 'Cash Mgmt',      'D.N.': +(88 + jitter()),    Norte: +(82 + jitter()),    Sur: +(79 + jitter()),    Este: +(86 + jitter())    },
  ]
}

interface ZoneRow { zone: ZoneName; total: number; avail: number; oos: number; pct: number; mttr: number; alerts: number; txns: number; sla: boolean }
type SortKey = keyof ZoneRow

function buildZoneTable(fleet: ATMDevice[], alerts: ATMAlert[]): ZoneRow[] {
  const r = rng32(1234567)
  const fMap = new Map<string, { total: number; avail: number; oos: number }>()
  fleet.forEach((d) => {
    const z = (d.zone as ZoneName) ?? 'Distrito Nacional'
    const e = fMap.get(z) ?? { total: 0, avail: 0, oos: 0 }
    e.total++
    if (d.status === 'IN_SERVICE') e.avail++
    if (d.status === 'OUT_OF_SERVICE') e.oos++
    fMap.set(z, e)
  })
  const aMap = new Map<string, number>()
  alerts.filter((a) => a.status === 'OPEN' || a.status === 'ACKNOWLEDGED').forEach((a) => {
    aMap.set(a.zone, (aMap.get(a.zone) ?? 0) + 1)
  })
  const DEFAULTS: Record<ZoneName, number> = { 'Distrito Nacional': 312, Norte: 241, Sur: 198, Este: 182 }
  return ZONE_NAMES.map((z) => {
    const e = fMap.get(z)
    const total = (e?.total ?? 0) || DEFAULTS[z]
    const avail = e?.avail ?? Math.round(total * (z === 'Este' ? 0.929 : z === 'Distrito Nacional' ? 0.920 : z === 'Norte' ? 0.892 : 0.889))
    const oos   = e?.oos   ?? (total - avail)
    const pct   = +(avail / total * 100).toFixed(1)
    return {
      zone: z, total, avail, oos, pct,
      mttr:   Math.round(100 + r() * 80),
      alerts: aMap.get(z) ?? Math.floor(r() * 15),
      txns:   Math.round(380_000 + r() * 280_000),
      sla:    pct >= 92,
    }
  })
}

// ─── Scorecard sparklines ─────────────────────────────────────────────────────

function Sparkline({ data, color }: { data: number[]; color: string }) {
  const n = data.length
  if (n < 2) return null
  const min = Math.min(...data)
  const max = Math.max(...data)
  const span = max - min || 1
  const W = 64, H = 22
  const pts = data.map((v, i) => `${(i / (n - 1)) * W},${H - ((v - min) / span) * (H - 3)}`).join(' ')
  return (
    <svg width={W} height={H} style={{ display: 'block', flexShrink: 0 }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth={1.8}
        strokeLinecap="round" strokeLinejoin="round" opacity={0.85} />
    </svg>
  )
}

// ─── ScoreCard ────────────────────────────────────────────────────────────────

interface ScoreProps {
  label: string; value: string | number; unit?: string
  delta?: number; color: string; sparkData?: number[]
  subtitle?: string
}

function ScoreCard({ label, value, unit = '', delta, color, sparkData, subtitle }: ScoreProps) {
  const C = useTheme()
  const up = (delta ?? 0) >= 0
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`, borderRadius: 12,
      padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 6,
      borderTop: `3px solid ${color}`,
    }}>
      <div style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
        <div>
          <span style={{ fontSize: 26, fontWeight: 700, color, lineHeight: 1 }}>{value}</span>
          {unit && <span style={{ fontSize: 13, color: C.muted, marginLeft: 2 }}>{unit}</span>}
          {subtitle && <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{subtitle}</div>}
        </div>
        {sparkData && <Sparkline data={sparkData} color={color} />}
      </div>
      {delta !== undefined && (
        <div style={{ fontSize: 11, color: up ? '#22c55e' : '#ef4444', display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ fontSize: 13 }}>{up ? '▲' : '▼'}</span>
          {Math.abs(delta).toFixed(1)}{unit} vs período anterior
        </div>
      )}
    </div>
  )
}

// ─── Panel wrapper ────────────────────────────────────────────────────────────

function Panel({ title, badge, sub, children, style }: {
  title: string; badge?: string; sub?: string; children: ReactNode; style?: CSSProperties
}) {
  const C = useTheme()
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '16px 18px', ...style }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{title}</div>
          {sub && <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{sub}</div>}
        </div>
        {badge && (
          <span style={{ fontSize: 10, color: C.muted, background: C.tint, padding: '3px 8px', borderRadius: 5, border: `1px solid ${C.border}` }}>
            {badge}
          </span>
        )}
      </div>
      {children}
    </div>
  )
}

// ─── Custom Tooltip ───────────────────────────────────────────────────────────

function ChartTip({ active, payload, label }: { active?: boolean; payload?: { name: string; value: number; color: string }[]; label?: string }) {
  const C = useTheme()
  if (!active || !payload?.length) return null
  return (
    <div style={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 14px', fontSize: 11, boxShadow: '0 4px 16px rgba(0,0,0,0.4)' }}>
      {label && <div style={{ color: C.muted, marginBottom: 6, fontWeight: 600 }}>{label}</div>}
      {payload.map((p) => (
        <div key={p.name} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, color: C.text }}>
          <span style={{ color: p.color }}>{p.name}</span>
          <span style={{ fontWeight: 700 }}>{typeof p.value === 'number' ? p.value.toLocaleString('es-DO') : p.value}</span>
        </div>
      ))}
    </div>
  )
}

// ─── Time-range button ────────────────────────────────────────────────────────

function RangeBtn({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  const C = useTheme()
  return (
    <button onClick={onClick} style={{
      background: active ? '#264E72' : C.tint,
      border: `1px solid ${active ? '#264E72' : C.border}`,
      color: active ? '#fff' : C.muted,
      borderRadius: 6, padding: '5px 12px', fontSize: 11, fontWeight: active ? 700 : 400,
      cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
    }}>{label}</button>
  )
}

// ─── Zone Comparative Table ───────────────────────────────────────────────────

function ZoneTable({ rows }: { rows: ZoneRow[] }) {
  const C = useTheme()
  const [sortKey, setSortKey] = useState<SortKey>('pct')
  const [asc, setAsc] = useState(false)

  const sorted = [...rows].sort((a, b) => {
    const va = a[sortKey], vb = b[sortKey]
    const cmp = typeof va === 'string' ? va.localeCompare(vb as string) : (va as number) - (vb as number)
    return asc ? cmp : -cmp
  })

  const thStyle = (key: SortKey, right = false): CSSProperties => ({
    background: '#1e2d3d', color: sortKey === key ? '#02AFF0' : C.muted,
    padding: '9px 12px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: 0.5, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
    textAlign: right ? 'right' : 'left',
    borderBottom: `2px solid ${sortKey === key ? '#02AFF0' : C.border}`,
  })
  const onThClick = (key: SortKey) => () => {
    if (sortKey === key) setAsc(!asc); else { setSortKey(key); setAsc(false) }
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr>
            {([ ['zone','Zona',false], ['total','Total ATMs',true], ['avail','En Servicio',true],
                ['oos','OOS',true], ['pct','Disp. %',true], ['mttr','MTTR',true],
                ['alerts','Alertas',true], ['txns','Transacciones',true], ['sla','SIB SLA',false],
              ] as [SortKey, string, boolean][]).map(([k, l, r]) => (
                <th key={k} style={thStyle(k, r)} onClick={onThClick(k)}>{l} {sortKey === k ? (asc ? '↑' : '↓') : ''}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, idx) => {
            const even = idx % 2 === 0
            const bg = even ? 'transparent' : C.tint
            const pctColor = row.pct >= 92 ? '#22c55e' : row.pct >= 88 ? '#F89421' : '#ef4444'
            return (
              <tr key={row.zone} style={{ background: bg }}>
                <td style={{ padding: '10px 12px', fontWeight: 700, color: ZONE_HEX[row.zone] }}>{row.zone}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', color: C.text }}>{row.total.toLocaleString('es-DO')}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', color: '#22c55e' }}>{row.avail.toLocaleString('es-DO')}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', color: row.oos > 0 ? '#ef4444' : C.muted }}>{row.oos}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: pctColor }}>{row.pct}%</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', color: row.mttr > 150 ? '#F89421' : C.text }}>{row.mttr} min</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', color: row.alerts > 10 ? '#ef4444' : C.text }}>{row.alerts}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', color: C.text }}>{row.txns.toLocaleString('es-DO')}</td>
                <td style={{ padding: '10px 12px' }}>
                  <span style={{
                    background: row.sla ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                    color: row.sla ? '#22c55e' : '#ef4444',
                    border: `1px solid ${row.sla ? '#22c55e44' : '#ef444444'}`,
                    borderRadius: 5, padding: '2px 8px', fontSize: 10, fontWeight: 700,
                  }}>{row.sla ? 'CUMPLE' : 'NO CUMPLE'}</span>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ─── Main view ────────────────────────────────────────────────────────────────

export default function AnalyticsView() {
  const C = useTheme()
  const { fleet, alerts, kpis } = useATMStore()

  const [range, setRange] = useState<TimeRange>('30d')

  // ── Derived data (all seeded — stable across renders) ──────────────────────
  const txData       = useMemo(() => buildTxSeries(range),            [range])
  const mttrData     = useMemo(() => buildMTTRSeries(range),          [range])
  const alertData    = useMemo(() => buildAlertSeries(range),         [range])
  const zoneAvail    = useMemo(() => buildZoneAvail(fleet),           [fleet])
  const compFaults   = useMemo(() => buildComponentFaults(fleet, range), [fleet, range])
  const radarData    = useMemo(() => buildRadar(fleet),               [fleet])
  const tableRows    = useMemo(() => buildZoneTable(fleet, alerts),   [fleet, alerts])

  // ── Scorecard deltas (current range vs same-length previous) ──────────────
  const txTotal      = txData.reduce((s, d) => s + d.txns, 0)
  const txAvgRate    = +(txData.reduce((s, d) => s + d.rate, 0) / txData.length).toFixed(1)
  const mttrAvg      = Math.round(mttrData.reduce((s, d) => s + d.mttr, 0) / mttrData.length)
  const alertTotal   = alertData.reduce((s, d) => s + d.P1 + d.P2 + d.P3 + d.P4, 0)
  const p1Total      = alertData.reduce((s, d) => s + d.P1, 0)
  const fleetAvail   = kpis?.availability_pct ?? 90.8

  // Sparklines (last 7 data points regardless of range)
  const txSpark      = txData.slice(-7).map((d) => d.txns)
  const mttrSpark    = mttrData.slice(-7).map((d) => d.mttr)
  const alertSpark   = alertData.slice(-7).map((d) => d.P1 + d.P2 + d.P3 + d.P4)
  const availSpark   = zoneAvail.map((z) => z.pct)

  const chartTick  = { fill: C.muted, fontSize: 10 }
  const chartGrid  = { strokeDasharray: '3 3', stroke: C.border }

  // Thin out labels for readability on 30/90d charts
  const tickInterval = range === '7d' ? 0 : range === '30d' ? 4 : 14

  return (
    <div style={{ flex: 1, overflow: 'auto', background: C.dark, padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.text }}>
            Analytics Intelligence Center
          </h2>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
            Red 933 Cajeros — Banreservas · Datos en vivo + análisis histórico
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 10, color: C.muted, marginRight: 4 }}>Período</span>
          {(['7d', '30d', '90d'] as TimeRange[]).map((r) => (
            <RangeBtn key={r} label={r === '7d' ? '7 días' : r === '30d' ? '30 días' : '90 días'} active={range === r} onClick={() => setRange(r)} />
          ))}
        </div>
      </div>

      {/* ── Scorecard row ─────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 10 }}>
        <ScoreCard label="Disponibilidad Red"   value={fleetAvail.toFixed(1)} unit="%" color="#22c55e"  delta={+0.4}  sparkData={availSpark}  subtitle="SIB meta ≥ 92%" />
        <ScoreCard label="Transacciones"        value={(txTotal / 1000).toFixed(0)} unit="k" color="#02AFF0"  delta={+3.2}  sparkData={txSpark}     subtitle={`${RANGE_DAYS[range]} días`} />
        <ScoreCard label="Tasa de Aprobación"   value={txAvgRate}   unit="%"  color="#a3e635" delta={+0.3}  sparkData={txData.slice(-7).map((d) => d.rate)} />
        <ScoreCard label="MTTR Promedio"        value={mttrAvg}     unit=" min" color="#F89421" delta={-8}    sparkData={mttrSpark} subtitle="Meta ≤ 120 min" />
        <ScoreCard label="Total Alertas"        value={alertTotal}  unit=""   color={p1Total > 20 ? '#ef4444' : '#F89421'} delta={-12} sparkData={alertSpark} />
        <ScoreCard label="Incidentes P1"        value={p1Total}     unit=""   color="#ef4444"  delta={p1Total < 20 ? -3 : +2} subtitle="Alta prioridad" />
      </div>

      {/* ── Row 1: Transaction Volume + Zone SLA ────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>

        {/* Transaction Volume */}
        <Panel title="Volumen de Transacciones" badge={`${RANGE_DAYS[range]} días`}
          sub={`${txTotal.toLocaleString('es-DO')} txns · ${txAvgRate}% aprobación`}>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={txData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="txGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#02AFF0" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#02AFF0" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="decGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ef4444" stopOpacity={0.18} />
                  <stop offset="95%" stopColor="#ef4444" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid {...chartGrid} />
              <XAxis dataKey="label" tick={chartTick} interval={tickInterval} />
              <YAxis tick={chartTick} tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip content={<ChartTip />} />
              <Area type="monotone" dataKey="txns"     name="Txns Totales" stroke="#02AFF0" fill="url(#txGrad)"  strokeWidth={2} dot={false} />
              <Area type="monotone" dataKey="declined" name="Declinadas"   stroke="#ef4444" fill="url(#decGrad)" strokeWidth={1.5} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </Panel>

        {/* Zone SLA Compliance */}
        <Panel title="Cumplimiento SLA por Zona" badge="Meta SIB ≥ 92%"
          sub="Disponibilidad actual vs objetivo regulatorio">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={zoneAvail} margin={{ top: 4, right: 12, left: -10, bottom: 0 }}>
              <CartesianGrid {...chartGrid} />
              <XAxis dataKey="zone" tick={chartTick} />
              <YAxis domain={[82, 100]} tick={chartTick} unit="%" />
              <Tooltip content={<ChartTip />} formatter={(v: number) => [`${v}%`, 'Disponibilidad']} />
              <ReferenceLine y={92} stroke="#22c55e" strokeDasharray="6 3" strokeWidth={1.5}
                label={{ value: '92% SIB', position: 'right', fill: '#22c55e', fontSize: 10 }} />
              <Bar dataKey="pct" name="Disponibilidad" radius={[6, 6, 0, 0]}>
                {zoneAvail.map((z) => (
                  <Cell key={z.zone} fill={z.pct >= 92 ? '#22c55e' : z.pct >= 88 ? '#F89421' : '#ef4444'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>

      </div>

      {/* ── Row 2: MTTR Trend + Alert Distribution ───────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>

        {/* MTTR Trend */}
        <Panel title="Tendencia MTTR" badge={`Prom. ${mttrAvg} min`}
          sub="Mean Time To Repair · línea roja = meta 120 min">
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={mttrData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="mttrGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#F89421" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#F89421" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid {...chartGrid} />
              <XAxis dataKey="label" tick={chartTick} interval={tickInterval} />
              <YAxis tick={chartTick} unit=" m" domain={[60, 240]} />
              <Tooltip content={<ChartTip />} formatter={(v: number) => [`${v} min`, 'MTTR']} />
              <ReferenceLine y={120} stroke="#ef4444" strokeDasharray="6 3" strokeWidth={1.5}
                label={{ value: 'meta 120', position: 'right', fill: '#ef4444', fontSize: 9 }} />
              <Area type="monotone" dataKey="mttr" name="MTTR" stroke="#F89421"
                fill="url(#mttrGrad)" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="mttr" name="MTTR" stroke="#F89421"
                strokeWidth={2} dot={{ r: 0 }} activeDot={{ r: 4, fill: '#F89421' }} />
            </ComposedChart>
          </ResponsiveContainer>
        </Panel>

        {/* Alert Distribution */}
        <Panel title="Distribución de Alertas" badge={`${alertTotal} alertas`}
          sub="Volumen diario por nivel de prioridad (P1–P4)">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={alertData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
              <CartesianGrid {...chartGrid} />
              <XAxis dataKey="label" tick={chartTick} interval={tickInterval} />
              <YAxis tick={chartTick} />
              <Tooltip content={<ChartTip />} />
              <Legend iconSize={8} wrapperStyle={{ fontSize: 10, color: C.muted }} />
              {(['P1', 'P2', 'P3', 'P4'] as const).map((p) => (
                <Bar key={p} dataKey={p} name={p} stackId="a"
                  fill={ALERT_HEX[p]} radius={p === 'P4' ? [3, 3, 0, 0] : [0, 0, 0, 0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Panel>

      </div>

      {/* ── Row 3: Component Faults + Fleet Health Radar ─────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>

        {/* Component Fault Profile */}
        <Panel title="Perfil de Fallas por Componente" badge={`${RANGE_DAYS[range]} días`}
          sub="Fallas acumuladas por subsistema · mayor → menor">
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={compFaults} layout="vertical" margin={{ top: 4, right: 40, left: 10, bottom: 0 }}>
              <CartesianGrid {...chartGrid} horizontal={false} />
              <XAxis type="number" tick={chartTick} />
              <YAxis type="category" dataKey="component" tick={{ ...chartTick, fontSize: 11, fontWeight: 700 }} width={36} />
              <Tooltip content={<ChartTip />} formatter={(v: number, name: string) => [`${v} fallas`, name]} />
              <Bar dataKey="faults" name="Fallas" radius={[0, 6, 6, 0]}>
                {compFaults.map((c) => (
                  <Cell key={c.component} fill={c.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          {/* Rate legend */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            {compFaults.map((c) => (
              <div key={c.component} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: C.muted }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: c.color, display: 'inline-block' }} />
                {c.component} <span style={{ color: C.text }}>{c.rate}%</span>
              </div>
            ))}
          </div>
        </Panel>

        {/* Fleet Health Radar */}
        <Panel title="Salud de Flota por Zona" badge="5 dimensiones"
          sub="Comparativa multi-dimensión: Disponibilidad · MTTR · Confiabilidad · Transacciones · Cash">
          <ResponsiveContainer width="100%" height={230}>
            <RadarChart data={radarData} margin={{ top: 8, right: 24, left: 24, bottom: 8 }}>
              <PolarGrid stroke={C.border} />
              <PolarAngleAxis dataKey="axis" tick={{ fill: C.muted, fontSize: 10 }} />
              <PolarRadiusAxis angle={90} domain={[70, 100]} tick={{ fill: C.muted, fontSize: 9 }} />
              {ZONE_NAMES.map((z) => (
                <Radar key={z} name={ZONE_SHORT[z]} dataKey={ZONE_SHORT[z]}
                  stroke={ZONE_HEX[z]} fill={ZONE_HEX[z]} fillOpacity={0.08}
                  strokeWidth={2} dot />
              ))}
              <Legend iconSize={8} wrapperStyle={{ fontSize: 10, color: C.muted }} />
              <Tooltip content={<ChartTip />} />
            </RadarChart>
          </ResponsiveContainer>
        </Panel>

      </div>

      {/* ── Zone Comparative Table ────────────────────────────────────────── */}
      <Panel title="Comparativa por Zona" badge="Clic en columna para ordenar"
        sub="Métricas completas · ordenable por cualquier columna">
        <ZoneTable rows={tableRows} />
      </Panel>

      {/* ── Bottom padding ────────────────────────────────────────────────── */}
      <div style={{ height: 8 }} />
    </div>
  )
}
